import React from 'react';
import { Link } from 'react-router';
import { Points } from 'components/Points/Points';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import UserHoverCard from 'components/UserHoverCard/UserHoverCard';
import { useHoverCard } from 'hooks/useHoverCard';
import { TradeAlertRequester } from 'store/services/tradeAlerts/tradeAlertsApi';
import './RequesterProfileBlock.css';

interface RequesterProfileBlockProps {
  requester: TradeAlertRequester;
  /** Extra class on the link wrapper (lets callers tweak spacing per context). */
  className?: string;
}

/**
 * The full-treatment requester identity block: avatar (animated Elite border +
 * country flag) + username with Elite badge + recipient point balance, all linking
 * to the profile and showing the UserHoverCard overlay on hover.
 *
 * Shared by the by-card AlertCard and the by-trader AlertTradeGroup header so the
 * profile chrome (and its hover wiring) lives in exactly one place.
 */
export const RequesterProfileBlock: React.FC<RequesterProfileBlockProps> = ({ requester, className }) => {
  // Hover overlay (trade metrics + badges) — same wiring as UsernameLink. Data is
  // fetched lazily by the hook against /users/:id/hover-card (no alerts-endpoint dep).
  const { triggerProps, cardProps, isVisible, position, data, isLoading } =
    useHoverCard(requester.username);

  return (
    <>
      <Link
        className={`requester-profile ${className || ''}`}
        to={`/profile/u/${requester.username}`}
        {...triggerProps}
      >
        <AvatarWithFlag
          avatarUrl={requester.avatarUrl}
          username={requester.username}
          size={48}
          countryCode={requester.country}
          isElite={requester.isElite}
          showAnimatedBorder={requester.showAnimatedBorder}
          borderColor1={requester.borderColor1}
          borderColor2={requester.borderColor2}
        />
        <span className="requester-profile-info">
          <span className="requester-profile-name">
            {requester.username}
            <EliteBadge isElite={!!requester.isElite} />
          </span>
          {requester.points != null && (
            <span className="requester-profile-points">
              <Points points={requester.points} type="small" />
            </span>
          )}
        </span>
      </Link>

      {isVisible && (
        <UserHoverCard
          data={data}
          isLoading={isLoading}
          position={position}
          cardProps={cardProps}
        />
      )}
    </>
  );
};

export default RequesterProfileBlock;
